import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import { formatDisplayHoursToMinutes, formatHours, untilDestroyed } from '@shared/utils/utils'

import { CommonModule } from '@angular/common'
import { CompanyManagementActions } from '@app/features/company-management/store/actions/company-management.actions'
import { CompanyManagementSelectors } from '@app/features/company-management/store/selectors/company-management.selectors'
import { DataMappingService } from '@app/features/scoping/service/data-mapping.service'
import { Department } from '@app/features/scoping/models/department.model'
import { LanguageService } from '@core/service/language.service'
import { LibraryTaskTemplate } from '@app/features/scoping/models/deliverable.model'
import { MappedEntity } from '@app/core/model/definitions/mapped-entity.interface'
import { MatDialog } from '@angular/material/dialog'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { ModalConfig } from '@core/model/modal-config.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { Observable } from 'rxjs'
import { Preference } from '@core/model/user-preferences.interface'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Role } from '@app/features/scoping/models/role.model'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { ScopeUiTableComponent } from '@app/shared/components/ui-components/scope-ui-table/scope-ui-table.component'
import { SharedModule } from '@app/shared/shared.module'
import { Store } from '@ngrx/store'
import { TableColumnKey } from '@shared/components/ui-components/scope-ui-table/table-column-key.enum'
import { User } from '@app/core/model/user.model'
import { cloneDeep } from 'lodash'

@Component({
  selector: 'scope-by-role-task-table',
  imports: [CommonModule, SharedModule, ScopeUiTableComponent],
  templateUrl: './scope-by-role-task-table.component.html',
  styleUrls: ['./scope-by-role-task-table.component.scss'],
  standalone: true,
})
export class ScopeByRoleTaskTableComponent implements OnDestroy, OnInit, OnChanges {
  private readonly destroy$

  @Input() currentUser!: User
  currentRatecard!: RatecardVersion
  currentDepartments!: Department[]
  outOfSync!: boolean

  @Input() set libraryTask(task: LibraryTaskTemplate) {
    if (task) {
      this.currentTask = task
      this.currentRatecard = task.rateCardVersion
      const deps = cloneDeep(
        task.rateCardVersion.departments.filter((d) =>
          d.roles.some((r) => this.currentTask.roles.some((taskRole) => taskRole.rateCardRole.id === r.id))
        )
      )
      deps.forEach(
        (d) => (d.roles = d.roles.filter((r) => this.currentTask.roles.map((tr) => tr.rateCardRole.id).includes(r.id)))
      )

      deps.forEach((dep) => {
        dep.roles = dep.roles.map((role) => {
          const matchedRole = this.currentTask.roles.find((r) => r.rateCardRole.id === role.id)
          return matchedRole ? matchedRole : role
        })
      })

      this.currentDepartments = deps
      this.mapDepartmentsDataSource(deps)
    }
  }

  ngOnChanges() {
    const searchText = this.searchText?.toLowerCase().trim()
    this.mapDepartmentsDataSource(
      this.currentDepartments
        .filter((department) => {
          if (!searchText) return true // Show all if search is empty
          department.roles = department.roles.filter((r) => r.rateCardRole.name.toLowerCase().includes(searchText))
          return department.roles.length > 0
        })
        .sort((a, b) => a.id - b.id)
    )
    this.setColumns()
  }

  @Input() tableExpanded: boolean
  @Output() tableExpandedChange = new EventEmitter<boolean>()
  @Input() searchText: string = ''
  @Output() outOfSyncChange = new EventEmitter<boolean>()

  currentTask: LibraryTaskTemplate
  currentTask$: Observable<LibraryTaskTemplate>
  mappedDepartmentsDataSource!: MappedEntity<Department>[]
  departmentColumns: Preference[]
  roleOptions: MenuOptions[]
  deleteDepartmentAction: any
  deleteRoleAction: any

  constructor(
    private store: Store,
    private mappingService: DataMappingService,
    private lang: LanguageService,
    public scopeTabService: ScopeTabService,
    private dialog: MatDialog
  ) {
    this.destroy$ = untilDestroyed()
    this.setColumns()
    this.currentTask$ = this.store.select(CompanyManagementSelectors.selectCurrentTask)
    this.deleteDepartmentAction = {
      clickAction: (department: Department) => this.deleteDepartmentModal(department),
      showOptionFunc: () => this.currentUser.hasPrivilege('LIBRARY_DELIVERABLE__EDIT'),
      buttonType: 'miniFab',
    }
    this.deleteRoleAction = {
      clickAction: (role: Role) => this.deleteRoleModal(role),
      showOptionFunc: () => !this.currentUser.hasPrivilege('LIBRARY_DELIVERABLE__EDIT'),
      buttonType: 'miniFab',
    }
  }

  ngOnInit() {
    this.outOfSync = false
  }

  mapDepartmentsDataSource(deps) {
    this.mappedDepartmentsDataSource = this.mappingService
      .transformArrayWithChildren(deps, this.departmentColumns, 'roles')
      .reduce((res, entity) => [...res, entity, ...entity.children], [])
  }

  setColumns() {
    this.departmentColumns = [
      {
        key: TableColumnKey.NAME,
        name: `DEPARTMENTS`,
        field: 'name',
        selected: true,
        noSort: false,
        value: (entity: Role | Department) => {
          if (entity instanceof Department) {
            return entity.name
          } else {
            return entity.rateCardRole.name
          }
        },
      },
      {
        key: TableColumnKey.LOCATION,
        name: `Location`,
        requiredSetting: 'RATECARD_LOCATION__ENABLE',
        isVisible: () => false, // Temporarily disabled due to Location state in app
        selected: false,
        value: (entity: Role | Department) => {
          if (entity instanceof Department) {
            return ''
          } else {
            if (entity.ratecardLocationRole && this.currentTask.rateCardVersion) {
              let locationCard = {
                ...this.currentTask.rateCardVersion.locationCards?.find(
                  (lc) => lc.roles.filter((r: Role) => r.id === entity.ratecardLocationRole.id).length > 0
                ),
              }
              return locationCard?.rateCardLocation?.name || 'None'
            } else {
              return 'None'
            }
          }
        },
        isDisabled: () => {
          return true
        },
        optionsFunction: (role: Role) => {
          if (!this.currentRatecard) return null
          return ['None'].concat(
            this.currentRatecard?.locationCards
              ?.filter((lc) => lc.roles.filter((r) => r.rateCardRole.id == role.rateCardRole.id).length > 0) //to add task role
              .map((lc) => lc.rateCardLocation.name)
          )
        },
        hideOptionsMenu: (entity: Role | Department) => {
          return entity instanceof Department
        },
        optionDisplayFn: (option: any) => option,
        onChange: (event: { value: any; element: Role }) => {
          let role = event.element
          if (event.value != 'None') {
            const lc = this.currentRatecard?.locationCards?.find((lc) => lc.rateCardLocation.name === event.value)
            let locationRole = { ...lc.roles.find((r) => r.rateCardRole.id == role.rateCardRole.id) }
            if (locationRole) {
              locationRole.locationCard = lc
              let update = { ...role, ratecardLocationRole: locationRole }
              this.store.dispatch(CompanyManagementActions.updateLibraryTaskRoleSuccess({ role: update }))
              this.outOfSync = true
              this.outOfSyncChange.emit(this.outOfSync)
            }
          } else {
            let update = { ...role, ratecardLocationRole: null }
            this.store.dispatch(CompanyManagementActions.updateLibraryTaskRoleSuccess({ role: update }))
            this.outOfSync = true
            this.outOfSyncChange.emit(this.outOfSync)
          }
        },
        noSort: true,
      },
      {
        key: TableColumnKey.AGENCY_RATE,
        name: `Rate`,
        value: (entity: Role | Department) => {
          if (entity instanceof Department) {
            return ''
          } else {
            if (entity.ratecardLocationRole) {
              return Money.of(entity.ratecardLocationRole?.rateAmount || 0, entity.rate.currency)
            }
            return entity.rate
          }
        },
        selected: true,
        noSort: true,
      },
      {
        key: TableColumnKey.FTE,
        name: `% FTE`,
        value: (entity: Role | Department) => {
          let totalHours = 0
          if (entity instanceof Department) {
            let hours = 0
            entity.roles.forEach((role) => {
              hours += role.minutes / 60
            })
            totalHours += hours != null ? hours : 0
          } else {
            totalHours += entity.minutes / 60
          }

          if (totalHours === 0) {
            return 0
          }
          return parseFloat(((totalHours / this.currentUser.company.fteValue) * 100).toFixed(2))
        },
        selected: true,
        noSort: false,
        inputType: (entity: Role | Department) => (entity instanceof Role ? 'number' : null),
        onInputChange: this.updateRoleFte,
        inputContext: { isDecimal: true, maxLength: 4 },
      },
      {
        key: TableColumnKey.HOURS,
        name: `Hours`,
        value: (entity: Role | Department) => {
          if (entity instanceof Department) {
            let totalHours = 0
            let hours = 0
            entity.roles.forEach((role) => {
              hours += role.minutes / 60
            })
            totalHours += hours != null ? hours : 0
            return formatHours(totalHours)
          } else {
            return entity.minutes
          }
        },
        selected: true,
        noSort: false,
        inputType: (entity: Role | Department) => (entity instanceof Role ? 'number' : null),
        onInputChange: this.updateRoleHours,
        inputContext: {
          isDecimal: true,
          enableMinors: true,
          majorStep: 60,
          minorStep: 15,
          max: 9999 * 60,
          maxLength: 4,
          formatDisplayValue: (val) => formatHours(val / 60),
          parseDisplayValue: (val) => formatDisplayHoursToMinutes(val),
        },
      },
      {
        key: TableColumnKey.AGENCY_PRICE,
        name: `${this.lang.get('agency')} Price`,
        value: (entity: Role | Department) => {
          if (entity instanceof Department) {
            let totalPrice = new Money(0, this.currentRatecard.currencyCode)
            for (let i = 0; i < entity.roles.length; i++) {
              let role = entity.roles[i]

              let userPrice = role.rate as Money
              if (role.ratecardLocationRole) {
                userPrice = Money.of(role.ratecardLocationRole?.rateAmount || 0, role.rate.currency)
              }

              if (userPrice == null) return null
              totalPrice = totalPrice.add(userPrice.multiply(role.minutes / 60))
            }
            return totalPrice
          } else {
            let totalPrice = new Money(0, entity.rate.currency)
            let userPrice = entity.rate as Money
            if (entity.ratecardLocationRole) {
              userPrice = Money.of(entity.ratecardLocationRole.rateAmount, entity.rate.currency)
            }
            totalPrice = totalPrice.add(userPrice.multiply(entity.minutes / 60))
            return totalPrice
          }
        },
        selected: true,
        noSort: false,
      },
    ]
  }

  calculateMinutesFromFte(fteValue: number | string): number {
    const parsedFteValue = typeof fteValue === 'string' ? parseFloat(fteValue) : fteValue
    if (isNaN(parsedFteValue) || parsedFteValue <= 0) {
      return 0
    }
    const companyFteValue = this.currentUser.company.fteValue
    if (companyFteValue === 0) {
      throw new Error('Company FTE value cannot be 0.') // Avoid division by 0
    }
    const totalHours = (parsedFteValue * companyFteValue) / 100
    return Math.round(totalHours * 60)
  }

  updateRoleFte = (role: Role, value: string) => {
    let minutes = this.calculateMinutesFromFte(value)
    let update = { ...role, minutes: minutes, hours: minutes / 60 }
    this.store.dispatch(CompanyManagementActions.updateLibraryTaskRoleSuccess({ role: update }))
    this.outOfSync = true
    this.outOfSyncChange.emit(this.outOfSync)
    this.setColumns()
  }

  updateRoleHours = (role, value) => {
    let update = { ...role, minutes: value, hours: value / 60 }
    this.store.dispatch(CompanyManagementActions.updateLibraryTaskRoleSuccess({ role: update }))
    this.outOfSync = true
    this.outOfSyncChange.emit(this.outOfSync)
    this.setColumns()
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: Event) {
    if (this.outOfSync) {
      event.preventDefault()
      event.returnValue = false
    }
  }

  deleteDepartmentModal(department: Department) {
    let description = 'This cannot be reverted.'
    if (this.outOfSync) {
      description += ' Your current changes will be synced with server.'
    }
    let dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete department "${department.name}"?`,
        `${description}`,
        'Delete',
        'Cancel',
        () => {
          dialog.close()
          const removedRoles = department.roles.map((r) => r.rateCardRole.id)
          if (this.outOfSync) {
            this.currentTask$.subscribe((task) => (this.currentTask = task))
          }
          const update = cloneDeep(this.currentTask)
          update.roles = [...this.currentTask.roles.filter((r) => !removedRoles.includes(r.rateCardRole.id))]
          this.store.dispatch(CompanyManagementActions.updateLibraryTask({ task: update }))
          this.outOfSync = false
          this.outOfSyncChange.emit(this.outOfSync)
        },
        () => {
          dialog.close()
        },
        undefined,
        undefined,
        undefined,
        true
      ),
    })
  }

  deleteRoleModal(role: Role) {
    let description = 'This cannot be reverted.'
    if (this.outOfSync) {
      description += ' Your current changes will be synced with server.'
    }
    let dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete role "${role.rateCardRole.name}"?`,
        `${description}`,
        'Delete',
        'Cancel',
        () => {
          dialog.close()
          const removedRole = role.rateCardRole.id
          if (this.outOfSync) {
            this.currentTask$.subscribe((task) => (this.currentTask = task))
          }
          const update = cloneDeep(this.currentTask)
          update.roles = [...update.roles.filter((r) => removedRole !== r.rateCardRole.id)]
          this.store.dispatch(CompanyManagementActions.updateLibraryTask({ task: update }))
          this.outOfSync = false
          this.outOfSyncChange.emit(this.outOfSync)
        },
        () => {
          dialog.close()
        },
        undefined,
        undefined,
        undefined,
        true
      ),
    })
  }

  ngOnDestroy() {
    if (!this.outOfSync) {
      return
    }
    this.outOfSync = false
    this.outOfSyncChange.emit(this.outOfSync)
    let dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `You have some unsaved changes`,
        'If you leave you would loose the changes',
        'Save',
        'Leave anyway',
        () => {
          dialog.close()
          this.store.dispatch(CompanyManagementActions.updateLibraryTask({ task: null }))
          this.store.dispatch(CompanyManagementActions.resetLibraryTask())
        },
        () => {
          dialog.close()
          this.store.dispatch(CompanyManagementActions.resetLibraryTask())
        },
        undefined,
        undefined,
        undefined,
        true
      ),
    })
  }
}
